import { z } from "zod";
import {
   CHANNEL_TYPE,
   CartesiaEmotions,
   CartesiaLevels,
   CartesiaSpeeds,
   PersonaStyles,
} from "@/enums/ai";
import { SupportedLanguages } from "@/enums/internationalization";
import { Industry, SystemTaskSubtype } from "@/enums/onboarding";
import { IconName } from "@fortawesome/fontawesome-common-types";
import { LangString } from "./api";
import { UseCaseAssociation } from "./scripts";

/* -------------------------------------------------------------------------- */
/*                                Shared Types                                */
/* -------------------------------------------------------------------------- */

export type FileUploadConfiguration = {
   enabled: boolean;
   allowedMimeTypes?: string[];
   maxFileSizeBytes?: number;
   maxFileCount?: number;
   totalMaxFileSizeBytes?: number;
};

/* -------------------------------------------------------------------------- */
/*                          Requests & Responses Types                        */
/* -------------------------------------------------------------------------- */

export type ObjectReviewRequest<T1, T2 = undefined> = {
   dataType: "script" | "useCase" | "sequence" | "redial" | "campaign" | "dataExtraction";
   action: "review" | "create" | "update";
   referenceData: T2;
   description: string;
   data: T1;
};

export type ReviewQuestion = {
   id: string;
   question: string;
   suggestedResponses: string[];
   /** When true, the user may select more than one suggested response. */
   allowMultiple: boolean;
   /** How the answer is collected. Omitted means suggested-answer chips plus free text. */
   inputType?: ReviewQuestionInputType;
};

export const REVIEW_QUESTION_INPUT_TYPES = ["choice", "multi", "text", "directory"] as const;

export type ReviewQuestionInputType = (typeof REVIEW_QUESTION_INPUT_TYPES)[number];

export type ObjectReviewResponse<T1, T2 = T1> = {
   original: T1;
   updated: T2;
   result: "good" | "updated" | "created";
   outcome: string;
   questions?: ReviewQuestion[];
};

type ToolsResponse = {
   success: boolean;
   labelKey: string;
   length: number;
   data?: any;
};

export type ToolsType = Record<
   string,
   {
      description: string;
      parameters: z.ZodTypeAny;
      execute: (params: any) => Promise<ToolsResponse>;
   }
>;

export type TranslateObjectRequest<T> = {
   object: T;
   targetLanguage: SupportedLanguages;
   targetType: "transcript" | "campaign_description";
};

/* -------------------------------------------------------------------------- */
/*                                 Personas                                   */
/* -------------------------------------------------------------------------- */

type PersonaElevenLabs = {
   voiceId: string;
   provider: "elevenlabs";
   languages: SupportedLanguages[];
};

type PersonaCartesia = {
   voiceId: string;
   provider: "cartesia";
   language: SupportedLanguages;
   speed: CartesiaSpeeds;
   emotion: CartesiaEmotions;
   level: CartesiaLevels;
};

type Persona = {
   name: string;
   styles: PersonaStyles[];
} & (PersonaElevenLabs | PersonaCartesia);

/* -------------------------------------------------------------------------- */
/*                              Assistant Tools                               */
/* -------------------------------------------------------------------------- */

type DataAssistantTool = { type: "data" };

type DocumentGenerationAssistantTool = { type: "document_generation" };

type HttpAssistantTool = {
   type: "http";
   id: string;
};

type UiAssistantTool = { type: "ui_components" };

export type QueryDocumentsAssistantTool = {
   type: "query";
   documentIds?: string[];
   tags?: string[];
};

export type WebSearchAssistantTool = {
   type: "web_search";
   domains?: string[];
};

type CodeExecutionAssistantTool = { type: "code_execution" };

type CloseConversationAssistantTool = { type: "close_conversation" };

type AssistantTaskTool = {
   type: "integration";
   id: string;
   name: string; // Display Name
   integration_id: string;
   options: object;
};

type AssistantTool =
   | DataAssistantTool
   | DocumentGenerationAssistantTool
   | HttpAssistantTool
   | AssistantTaskTool
   | UiAssistantTool
   | QueryDocumentsAssistantTool
   | WebSearchAssistantTool
   | CodeExecutionAssistantTool
   | CloseConversationAssistantTool;

export type IntegrationAssistantTool = {
   id: string;
   name: string;
   description: string;
   source?: "http" | "third_party" | "unknown";
   unavailable?: boolean;
};

/* -------------------------------------------------------------------------- */
/*                          Assistant Channels & Models                       */
/* -------------------------------------------------------------------------- */

// Catalog aliases are strings. Visible / deprecated / provider come from Galactus.
export type AssistantModel = string;

export const AUTO_ASSISTANT_MODEL = "auto";

// Default model for new assistants and fallback references
export const DEFAULT_ASSISTANT_MODEL = AUTO_ASSISTANT_MODEL;

// Keep trial onboarding seeds aligned with assistants users create manually.
export const TRIAL_SEEDED_ASSISTANT_MODEL = DEFAULT_ASSISTANT_MODEL;

// Channels

/**
 * Per-channel idle reminder configuration. When enabled, the platform
 * sends a follow-up message after `reminderAfter` of inactivity. The
 * timer resets on each user reply. `reminderAfter` must be less than
 * the configured channel TTL (validated server-side) and >= 5 minutes
 * (product floor — see IdleReminderSection).
 *
 * Backed by `/v1/assistants/{assistantId}/conversation-idle-reminders`.
 * Applies to SMS / Chat / WhatsApp only — GPT (web) is intentionally
 * excluded.
 */
export type AssistantIdleReminderSettings = {
   enabled: boolean;
   /** `hh:mm:ss` TimeSpan, e.g. "00:05:00" for 5 minutes. */
   reminderAfter: string | null;
   /** Plain text — backend stores NonEmptyString, not a multi-language object. */
   message: string | null;
};

export type AssistantChatChannel = {
   type: CHANNEL_TYPE.CHAT;
   fileUpload: FileUploadConfiguration;
   greeting: LangString;
   allowedUrls: string[] | null;
   idleReminder?: AssistantIdleReminderSettings;
};

export type AssistantGptChannel = {
   type: CHANNEL_TYPE.GPT;
   fileUpload: FileUploadConfiguration;
   greeting: LangString;
};

type AssistantSmsChannel = {
   type: CHANNEL_TYPE.SMS;
   fileUpload: FileUploadConfiguration;
   greeting: LangString;
   idleReminder?: AssistantIdleReminderSettings;
};

export type AssistantWhatsAppChannel = {
   type: CHANNEL_TYPE.WHATSAPP;
   fileUpload: FileUploadConfiguration;
   greeting: LangString;
   idleReminder?: AssistantIdleReminderSettings;
};

export type AssistantChannel = {
   model: AssistantModel;
} & (AssistantChatChannel | AssistantGptChannel | AssistantSmsChannel | AssistantWhatsAppChannel);

/* -------------------------------------------------------------------------- */
/*                       Instructions & Assistant Tasks                       */
/* -------------------------------------------------------------------------- */

export type UseCaseInstructions = {
   type: "use_case";
   useCaseAssociations: UseCaseAssociation[];
};

export type CustomInstructions = {
   type: "custom";
   instructions: string;
};

export type SystemTaskInstructions = {
   type: "system_task";
   systemTaskId: string;
};

export type AssistantInstructions =
   | UseCaseInstructions
   | CustomInstructions
   | SystemTaskInstructions;

export type AssistantTask = {
   id: string;
   name: string;
   description: string;
   handoffInstructions: string;
   tools: AssistantTool[];
   instructions: AssistantInstructions | AssistantInstructions[];
};

/* -------------------------------------------------------------------------- */
/*                                System Tasks                                */
/* -------------------------------------------------------------------------- */

export type SystemTask = {
   id: string;
   name: LangString;
   description: LangString;
   handoffInstructions: LangString;
   hint: LangString;
   industries: Industry[];
   icon: IconName | "";
   prompt: LangString;
   status: "active" | "archived";
   subtypes?: SystemTaskSubtype[];
};

export type SystemTaskSearchResult = {
   id: string;
   name: string;
   description: string;
   handoffInstructions: string;
   hint: string;
   industries: Industry[];
   icon: IconName;
   subtypes?: SystemTaskSubtype[];
};

/* -------------------------------------------------------------------------- */
/*                            Assistant & Related                             */
/* -------------------------------------------------------------------------- */

export type Assistant = {
   id: string;
   status: "active" | "inactive" | "archived";
   /* Descriptions */
   name: string;
   description: string;
   /* Metadata */
   persona: Persona;
   /* Associations and References */
   tasks: AssistantTask[];
   initialTaskId: string;
   channels: AssistantChannel[];
   /* Dates */
   createdAt: string;
   lastModifiedAt: string;
};

// Conversation Starters API Types
export interface ConversationStartersRequest {
   assistantId: string;
   assistantName: string;
   assistantDescription: string;
   assistantSpecialty?: string;
   capabilities?: string;
}

export interface ConversationStartersResponse {
   assistantId: string;
   suggestions: string[];
}
